import React, { useEffect, useState } from 'react';
import { Alert, Card, Select, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import { ApartmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { getOrganizationCenterSpecialities, getOrganizationManagementCenters, putOrganizationCenterSpeciality } from '../store/user/thunks';

const { Text } = Typography;

interface Center { id: string; name: string; status?: string; }
interface CenterSpeciality {
    specialityId: string;
    name: string;
    organizationStatus?: string;
    isAssociated: boolean;
    status: string;
}

interface Props {
    organizationId: string;
    getOrganizationManagementCenters?: any;
    getOrganizationCenterSpecialities?: any;
    putOrganizationCenterSpeciality?: any;
}

const normalizeCenters = (source: any): Center[] => {
    const values = Array.isArray(source) ? source : source?.centers || source?.data || [];
    return Array.isArray(values) ? values.map((item: any) => ({
        id: String(item.id || item.Id || ''),
        name: String(item.name || item.Name || 'Centro'),
        status: String(item.status || item.Status || 'ACTIVO')
    })).filter(item => item.id) : [];
};

const normalizeSpecialities = (source: any): CenterSpeciality[] => {
    const values = Array.isArray(source) ? source : source?.specialities || source?.data || [];
    return Array.isArray(values) ? values.map((item: any) => ({
        specialityId: String(item.specialityId || item.SpecialityId || ''),
        name: String(item.name || item.Name || 'Especialidad'),
        organizationStatus: item.organizationStatus || item.OrganizationStatus,
        isAssociated: Boolean(item.isAssociated ?? item.IsAssociated),
        status: String(item.status || item.Status || 'INACTIVO').toUpperCase()
    })).filter(item => item.specialityId) : [];
};

const OrganizationCenterSpecialityManagement: React.FC<Props> = props => {
    const [centers, setCenters] = useState<Center[]>([]);
    const [selectedCenterId, setSelectedCenterId] = useState<string>();
    const [specialities, setSpecialities] = useState<CenterSpeciality[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<string>();
    const [error, setError] = useState<string>();

    const loadSpecialities = async (centerId?: string) => {
        if (!centerId) {
            setSpecialities([]);
            return;
        }
        setLoading(true);
        setError(undefined);
        try {
            const response = await props.getOrganizationCenterSpecialities?.(centerId);
            setSpecialities(normalizeSpecialities(response));
        } catch (loadError) {
            setSpecialities([]);
            setError(loadError instanceof Error ? loadError.message : 'No se pudieron cargar las especialidades de la sucursal.');
        } finally {
            setLoading(false);
        }
    };

    const loadCenters = async () => {
        setError(undefined);
        try {
            const response = await props.getOrganizationManagementCenters?.(props.organizationId);
            const nextCenters = normalizeCenters(response);
            setCenters(nextCenters);
            const nextCenterId = nextCenters.some(item => item.id === selectedCenterId)
                ? selectedCenterId
                : nextCenters[0]?.id;
            setSelectedCenterId(nextCenterId);
            await loadSpecialities(nextCenterId);
        } catch (loadError) {
            setCenters([]);
            setSpecialities([]);
            setError(loadError instanceof Error ? loadError.message : 'No se pudieron cargar las sucursales.');
        }
    };

    useEffect(() => {
        loadCenters();
        // The organization id identifies the scope of this settings tab.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.organizationId]);

    const toggleSpeciality = async (row: CenterSpeciality, checked: boolean) => {
        if (!selectedCenterId) return;
        if (checked && row.organizationStatus?.toUpperCase() !== 'ACTIVO') {
            message.warning('Active primero esta especialidad en la organización.');
            return;
        }
        setUpdatingId(row.specialityId);
        setError(undefined);
        try {
            const response = await props.putOrganizationCenterSpeciality?.(selectedCenterId, {
                SpecialityId: row.specialityId,
                Status: checked ? 'ACTIVO' : 'INACTIVO'
            });
            if (response) {
                message.success(checked ? 'Especialidad activada en la sucursal.' : 'Especialidad desactivada en la sucursal.');
                await loadSpecialities(selectedCenterId);
            }
        } catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : 'No se pudo actualizar la especialidad de la sucursal.');
        } finally {
            setUpdatingId(undefined);
        }
    };

    return <Card
        title={<Space><ApartmentOutlined /> Especialidades por sucursal</Space>}
        extra={<Space>
            <ButtonRefresh onClick={() => loadCenters()} />
        </Space>}
    >
        <Text type="secondary">Active o desactive las especialidades disponibles en cada centro médico.</Text>
        {error && <Alert style={{ marginTop: 16 }} type="error" showIcon message="No se pudo cargar la configuración" description={error} closable onClose={() => setError(undefined)} />}
        {!centers.length ? <Alert style={{ marginTop: 16 }} type="warning" showIcon message="No hay sucursales registradas." description="Cree una sucursal antes de configurar sus especialidades." /> : <>
            <Select
                style={{ width: '100%', maxWidth: 420, marginTop: 16 }}
                value={selectedCenterId}
                onChange={value => { setSelectedCenterId(value); loadSpecialities(value); }}
                options={centers.map(center => ({ value: center.id, label: center.name }))}
                placeholder="Seleccione una sucursal"
            />
            {loading ? <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div> : <Table
                style={{ marginTop: 16 }}
                rowKey="specialityId"
                dataSource={specialities}
                pagination={false}
                locale={{ emptyText: 'No hay especialidades asociadas a la organización.' }}
                columns={[
                    { title: 'Especialidad', dataIndex: 'name', render: (value: string) => <Text strong>{value}</Text> },
                    { title: 'Organización', dataIndex: 'organizationStatus', render: (value: string) => <Tag color={value?.toUpperCase() === 'ACTIVO' ? 'green' : 'default'}>{value || 'INACTIVO'}</Tag> },
                    { title: 'Disponible en sucursal', dataIndex: 'status', render: (_: string, row: CenterSpeciality) => <Space>
                        <Switch
                            checked={row.isAssociated && row.status === 'ACTIVO'}
                            disabled={row.organizationStatus?.toUpperCase() !== 'ACTIVO'}
                            loading={updatingId === row.specialityId}
                            onChange={checked => toggleSpeciality(row, checked)}
                        />
                        <Tag color={row.isAssociated && row.status === 'ACTIVO' ? 'green' : 'default'}>
                            {row.isAssociated && row.status === 'ACTIVO' ? 'ACTIVA' : 'INACTIVA'}
                        </Tag>
                    </Space> }
                ]}
            />}
        </>}
    </Card>;
};

const ButtonRefresh: React.FC<{ onClick: () => void }> = ({ onClick }) => <button type="button" onClick={onClick} style={{ border: '1px solid #d9d9d9', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}><ReloadOutlined /> Actualizar</button>;

export default connect(null, {
    getOrganizationManagementCenters,
    getOrganizationCenterSpecialities,
    putOrganizationCenterSpeciality
})(OrganizationCenterSpecialityManagement);
